<script setup lang="ts">
import type { TimeRangeController } from '@/composables/useTimeRange'
import type { TimeRange } from '@/lib/charts/timeRange'
import { CalendarDate, getLocalTimeZone } from '@internationalized/date'
import { format } from 'date-fns'
import { computed, ref, watch } from 'vue'
import { injectTimeRange } from '@/composables/useTimeRange'
import {
  activePreset,
  isRelative,
  RANGE_PRESETS,
  toAbsolute,
} from '@/lib/charts/timeRange'
import RefreshControl from './RefreshControl.vue'

const props = defineProps<{
  controller?: TimeRangeController
}>()

const controller = props.controller ?? injectTimeRange()

const open = ref(false)

const rangeLabel = computed(() => {
  const preset = activePreset(controller.range.value)
  if (preset)
    return preset.label

  const { from, to } = controller.resolved.value
  return `${format(from, 'd MMM, HH:mm')} – ${format(to, 'd MMM, HH:mm')}`
})

const live = computed(() => isRelative(controller.range.value))

function toCalendarDate(ms: number) {
  const date = new Date(ms)
  return new CalendarDate(date.getFullYear(), date.getMonth() + 1, date.getDate())
}

// A draft selection the calendar mutates freely; nothing is applied until the
// user confirms, so picking a start date no longer fires a premature reload.
const draftStart = ref<CalendarDate | undefined>()
const draftEnd = ref<CalendarDate | undefined>()

watch(open, (isOpen) => {
  if (!isOpen)
    return
  const { from, to } = controller.resolved.value
  draftStart.value = toCalendarDate(from)
  draftEnd.value = toCalendarDate(to)
})

const calendarModel = computed({
  get: () => ({ start: draftStart.value, end: draftEnd.value }),
  set: ({ start, end }: { start: CalendarDate | undefined, end: CalendarDate | undefined }) => {
    draftStart.value = start
    draftEnd.value = end
  },
})

const canApply = computed(() => Boolean(draftStart.value && draftEnd.value))

// Render the selected (and range-preview) span as one continuous band instead of
// lone circles: every in-range day fills its cell edge-to-edge with square inner
// corners, and only the span's endpoints keep a rounded (pill) outer edge. The
// same treatment is applied to the live highlight while picking a range; rounding
// its row ends (highlighted-start/-end) keeps the moving cursor end and wrapped
// ranges clean. The default theme already shows a full-cell hover band for the
// preview, so a plain hover over a non-band day is widened to match instead of
// leaving a stray narrow circle beside the band.
const calendarUi = {
  cellTrigger: [
    'data-[highlighted]:w-full! data-[highlighted]:m-0 data-[highlighted]:rounded-none',
    'data-[selected]:w-full! data-[selected]:m-0 data-[selected]:rounded-none',
    'data-[selection-start]:rounded-l-full! data-[selection-end]:rounded-r-full!',
    'data-[highlighted-start]:rounded-l-full! data-[highlighted-end]:rounded-r-full!',
    'hover:not-data-[selected]:not-data-[highlighted]:w-full! hover:not-data-[selected]:not-data-[highlighted]:m-0',
  ],
}

function selectPreset(range: TimeRange) {
  controller.setRange(range)
  open.value = false
}

function applyDraft() {
  if (!draftStart.value || !draftEnd.value)
    return
  controller.setRange(toAbsolute({
    from: draftStart.value.toDate(getLocalTimeZone()).getTime(),
    to: draftEnd.value.toDate(getLocalTimeZone()).setHours(23, 59, 59, 999),
  }))
  open.value = false
}
</script>

<template>
  <div class="flex flex-wrap items-center gap-2">
    <UPopover v-model:open="open" :content="{ align: 'end' }">
      <UButton color="neutral" variant="subtle" icon="i-lucide-calendar-clock" :label="rangeLabel" trailing-icon="i-lucide-chevron-down" />

      <template #content>
        <div class="divide-default flex flex-col sm:flex-row sm:divide-x">
          <div class="flex max-h-96 flex-col overflow-y-auto py-1">
            <p class="text-muted px-4 py-1.5 text-xs font-medium uppercase tracking-wide">
              Quick ranges
            </p>
            <UButton
              v-for="preset in RANGE_PRESETS"
              :key="preset.label"
              :label="preset.label"
              color="neutral"
              variant="ghost"
              class="justify-start rounded-none px-4"
              :class="activePreset(controller.range.value)?.label === preset.label ? 'bg-elevated' : ''"
              @click="selectPreset(preset.range)"
            />
          </div>

          <div class="flex flex-col gap-3 p-3">
            <p class="text-muted text-xs font-medium uppercase tracking-wide">
              Select a time range
            </p>
            <UCalendar v-model="calendarModel" range :ui="calendarUi" />
            <UButton label="Apply" color="primary" block size="sm" :disabled="!canApply" @click="applyDraft" />
          </div>
        </div>
      </template>
    </UPopover>

    <UFieldGroup>
      <UButton icon="i-lucide-chevron-left" color="neutral" variant="subtle" aria-label="Earlier" @click="controller.stepBackward()" />
      <UButton icon="i-lucide-zoom-out" color="neutral" variant="subtle" aria-label="Zoom out" @click="controller.zoomOut()" />
      <UButton icon="i-lucide-chevron-right" color="neutral" variant="subtle" aria-label="Later" :disabled="live" @click="controller.stepForward()" />
    </UFieldGroup>

    <RefreshControl
      :model-value="controller.refresh.value"
      :loading="controller.loading.value"
      @update:model-value="controller.setRefresh($event)"
      @refresh="controller.reload()"
    />
  </div>
</template>
